/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

// import { ArrayExtensions, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IBuyboxAddToCartViewProps,
    IBuyboxAddToOrderTemplateViewProps,
    IBuyboxKeyInPriceViewProps,
    IBuyboxProductConfigureDropdownViewProps,
    IBuyboxProductConfigureViewProps,
    IBuyboxProductQuantityViewProps,
    IBuyboxShopSimilarLookViewProps
} from '@msdyn365-commerce-modules/buybox/src/modules/buybox/../../common';
import { IBuyboxViewProps } from '@msdyn365-commerce-modules/buybox/src/modules/buybox/./buybox';
import { IBuyboxFindInStoreViewProps } from '@msdyn365-commerce-modules/buybox/src/modules/buybox/./components/buybox-find-in-store';
import { configure } from 'mobx';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';

configure({ isolateGlobalState: true });

const BuyboxView: React.FC<IBuyboxViewProps> = props => {
    const {
        ModuleProps,
        MediaGalleryContainerProps,
        ProductInfoContainerProps,
        addToCart,
        addToOrderTemplate,
        configure,
        description,
        findInStore,
        quantity,
        price,
        title,
        rating,
        inventoryLabel,
        shopSimilarLook,
        keyInPrice,
        shopSimilarDescription,
        unitOfMeasure,
        data
    } = props;

    const deliveryOptions = props.data.deliveryOptions.result?.DeliveryOptions;
    let showAddToCartButton = true;

    if (deliveryOptions && deliveryOptions.length > 0) {
        const isOnlyBopis = deliveryOptions[0].ExtensionProperties?.find(y => y.Key === 'isOnlyBOPIS')?.Value?.BooleanValue;
        showAddToCartButton = !isOnlyBopis;
    }

    //@ts-ignore
    const productData = data.productSpecificationData.result;

    return (
        <Module {...ModuleProps}>
            <Node {...MediaGalleryContainerProps}>{props.mediaGallery}</Node>
            <Node {...ProductInfoContainerProps}>
                {title}
                {price}
                {unitOfMeasure}
                {description}
                {rating}
                {configure && _renderConfigure(configure, productData)}
                {keyInPrice && _renderKeyInPrice(keyInPrice)}
                {quantity && _renderQuantity(quantity)}
                {inventoryLabel}
                {_renderCheckoutActions(showAddToCartButton, addToCart, findInStore)}
                {addToOrderTemplate && _renderAddToOrderTemplate(addToOrderTemplate)}
                {shopSimilarLook && _renderShopSimilarItem(shopSimilarLook)}
                {shopSimilarDescription && _renderShopSimilarItem(shopSimilarDescription)}
                {_renderMediaLinks(props)}
            </Node>
        </Module>
    );
};

const _renderCheckoutActions = (
    showAddToCartButton: boolean,
    addToCart: IBuyboxAddToCartViewProps,
    findInStore: IBuyboxFindInStoreViewProps | undefined
) => (
    <>
        {showAddToCartButton && addToCart && _renderAddToCart(addToCart)}
        {findInStore && _renderFindInStore(findInStore)}
    </>
);

const _renderMediaLinks = (props: IBuyboxViewProps) => {
    // @ts-ignore
    const productSpecificationData = props.data?.productSpecificationData?.result;
    const mediaLinks: AttributeValue[] = [];
    productSpecificationData?.map((productData: AttributeValue) => {
        if (productData?.TextValue?.startsWith('http')) {
            mediaLinks.push(productData);
        }
    });
    return (
        <>
            {mediaLinks.length > 0 ? (
                <div>
                    <h2>Other Options</h2>
                    {mediaLinks.map(link =>
                        link.TextValue ? (
                            <div>
                                <a href={link.TextValue}>{link.Name}</a>
                            </div>
                        ) : (
                            ''
                        )
                    )}
                </div>
            ) : (
                ''
            )}
        </>
    );
};

const _renderAddToCart = (addToCart: IBuyboxAddToCartViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToCart;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderAddToOrderTemplate = (addToOrderTemplate: IBuyboxAddToOrderTemplateViewProps): JSX.Element => {
    const { ContainerProps, errorBlock, button } = addToOrderTemplate;

    return (
        <Node {...ContainerProps}>
            {errorBlock}
            {button}
        </Node>
    );
};

const _renderConfigure = (configure: IBuyboxProductConfigureViewProps, productSpecificationData: any): JSX.Element => {
    const { ContainerProps, dropdowns } = configure;

    const dimColor = productSpecificationData.find((x: { Name: string }) => x.Name === 'DimColor');
    const dimSize = productSpecificationData.find((x: { Name: string }) => x.Name === 'dimSize');
    const dimStyle = productSpecificationData.find((x: { Name: string }) => x.Name === 'dimStyle');
    const dimConfiguration = productSpecificationData.find((x: { Name: string }) => x.Name === 'dimConfiguration');

    dropdowns.map(x => {
        // @ts-ignore
        if (x?.heading?.props.children === 'Color') {
            if (dimColor?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimColor.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `Choose a ${dimColor.TextValue}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        // @ts-ignore
        if (x?.heading?.props.children === 'Size') {
            if (dimSize?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimSize.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `Choose a ${dimSize.TextValue}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        // @ts-ignore
        if (x?.heading?.props.children === 'Style') {
            if (dimStyle?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimStyle.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `Choose a ${dimStyle.TextValue}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        // @ts-ignore
        if (x?.heading?.props.children === 'Configuration') {
            if (dimConfiguration?.TextValue) {
                const headingCopy = React.cloneElement(x.heading as React.ReactElement, { children: dimConfiguration.TextValue });
                const selectCopy = React.cloneElement(x.select as React.ReactElement, {
                    dropdownToggleName: `Choose a ${dimConfiguration.TextValue}`
                });
                x.heading = headingCopy;
                x.select = selectCopy;
            }
        }
        return x;
    });

    return <Node {...ContainerProps}>{dropdowns.map(_renderConfigureDropdown)}</Node>;
};

const _renderConfigureDropdown = (dropdown: IBuyboxProductConfigureDropdownViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, errors, select } = dropdown;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {select}
        </Node>
    );
};

const _renderFindInStore = (findInStore: IBuyboxFindInStoreViewProps): JSX.Element => {
    const { ContainerProps, storeSelector, heading, errors, button, modal } = findInStore;

    return (
        <Node {...ContainerProps}>
            {storeSelector}
            {heading}
            {errors}
            {button}
            {modal}
        </Node>
    );
};

const _renderQuantity = (quantity: IBuyboxProductQuantityViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input, errors } = quantity;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>
                {heading}
                {errors}
            </Node>
            {input}
        </Node>
    );
};

const _renderKeyInPrice = (keyInPrice: IBuyboxKeyInPriceViewProps): JSX.Element => {
    const { ContainerProps, LabelContainerProps, heading, input } = keyInPrice;

    return (
        <Node {...ContainerProps}>
            <Node {...LabelContainerProps}>{heading}</Node>
            {input}
        </Node>
    );
};

const _renderShopSimilarItem = (shopSimilarItem: IBuyboxShopSimilarLookViewProps): JSX.Element => {
    const { ContainerProps, errors, input } = shopSimilarItem;

    return (
        <Node {...ContainerProps}>
            {errors}
            {input}
        </Node>
    );
};
export default BuyboxView;
